<template>
  <div class="home">
    <Header></Header>
    <section class="intro">
      <div class="container">
              <p class="center">
        <span>vue-kinesis</span> is a set of components that allow you to create interactive animations for your <span class="vue">Vue.js</span> applications.
      </p>

      <h2 class="center">How to use</h2>
      <p class="code">
        npm install -i <span class="vue">vue-kinesis</span>
      </p>

      <p class="code">
        import
        <span class="vue">{ KinesisContainer, KinesisElement}</span> from
        <span class="vue">‘vue-kinesis’</span>
      </p>

      <p class="code">
         <pre>
          <span class="vue">&lt;kinesis-container></span>
              Here, you can put
              <span class="vue">&lt;kinesis-element</span> :strength="10"<span class="vue">></span>
                whatever
              <span class="vue">&lt;/kinesis-element></span>
              <span class="vue">&lt;kinesis-element</span> :strength="20"<span class="vue">></span>
                content!
              <span class="vue">&lt;/kinesis-element></span>
         <span class="vue"> &lt;/kinesis-container></span>
        </pre>
      </p>
      <p class="center">
        This is the most basic way to use the components.
      </p>
      <p class="center">
        For the effect to work <span class="vue">&lt;kinesis-element></span> should be a child of <span class="vue">&lt;kinesis-container></span>
        <br>
        <br>
        They are <span>not</span> required to be <span>direct childs</span> though.
        <br>
This is quite handy when you have complex markups, such as <span>svg</span> .
      </p>
      </div>
    </section>
    <section class="examples">
      <div class="container">
              <h2 class="center">Examples</h2>
      <p class="center">Okay! Let's go! <br><br> We'll see how we can use these components in real life situations.
      <br>
      <br>
      Let's imagine a scenario where I'd like to tell you more about myself.</p>
      </div>

      <ExSection class="example-section" :bubble="sections.musics.bubble" :color="sections.musics.color">
        <KinesisScroll type="scale" class="circle circle-blue"></KinesisScroll>
        <ExMusic />
      </ExSection>

      <div class="container">
        <p>Our focus is on the album covers section. A lot of things are happening there.</p>
              <p class="code">
         <pre>
          <span class="vue">&lt;kinesis-container</span> :audio="audio"<span class="vue">></span>
              <span class="vue">&lt;kinesis-audio</span> 
                :audioIndex="10"<span class="vue">></span>
                  <span class="vue">&lt;kinesis-element</span> 
                    :strength="10" 
                    type="depth"<span class="vue">></span>
                  ...
                <span class="vue">&lt;/kinesis-element></span>
                <span class="vue">&lt;/kinesis-audio></span>
              ...
         <span class="vue"> &lt;/kinesis-container></span>
        </pre>
      </p>
              <p>This is a simplified version of the code.</p>
              <p>We have a <span class="vue">&lt;kinesis-audio></span> element wrapping a <span class="vue">&lt;kinesis-element></span> for each layer.</p>

              <p><span class="vue">&lt;kinesis-audio></span> reacts to a certain frequency of a given sound.</p>
              <p>For <span class="vue">&lt;kinesis-audio></span> to react you need to pass an audio file to the <span>audio</span> prop of <span class="vue">&lt;kinesis-container></span></p>
              <p>Then, you should tell <span class="vue">&lt;kinesis-audio></span> to which <span>frequency</span> to react, on a range that goes from <span>0 to 127</span> through the <span>audioIndex</span> prop</p>
      </div>

      <ExSection class="example-section" :bubble="sections.photography.bubble" :color="sections.photography.color">
        <KinesisScroll type="translate" :strength="-50" class="circle circle-purple"></KinesisScroll>
        <KinesisScroll type="translate" :strength="-200" class="circle circle-purple-2"></KinesisScroll>
        <KinesisScroll type="translate" :strength="-30" class="circle circle-purple-3"></KinesisScroll>

        <ExPhotography />
      </ExSection>

      <div class="container">
        <p>
        A simpler demo that involves different tricks: 
        </p>
                  <p class="code">
         <pre>
          <span class="vue">&lt;kinesis-container></span>
              <span class="vue">&lt;kinesis-element</span> 
                  tag="img"
                  :src="spider"
                  :strength="-10"
                  type="rotate"
                  transformOrigin="50% 300%"
                  axis="x"
                  type="depth"<span class="vue"> /></span>
            <span class="vue">&lt;kinesis-element</span> 
                  tag="img"
                  :src="ladybug"
                  :strength="10"
                  type="rotate"
                  transformOrigin="50% 300%"
                  axis="x"
                  type="depth"<span class="vue"> /></span>
            <span class="vue">&lt;kinesis-element</span> 
                  tag="img"
                  :src="fennec"
                  :strength="10"
                  type="depth"
                  transformOrigin="50% 300%"
                  type="depth"<span class="vue"> /></span>
         <span class="vue"> &lt;/kinesis-container></span>
        </pre>
      </p>
      <p>Each <span class="vue">&lt;kinesis-element></span> is an <span>img</span></p>
      <p>The Fennec picture has a <span>"depth"</span> transform type. The two other pictures have a <span>"rotate"</span> transform type.</p>
      <p>The key to this effect are the <span>transformOrigin</span> and <span>axis</span> props</p>
      <p><span>axis="x"</span> makes the elements react to movements along the x axis</p>
      <p><span>transformOrigin="50% 300%"</span> gives this nice card spreading movement to the elements.</p>
      </div>
      <ExSection class="example-section" :bubble="sections.bike.bubble" :color="sections.bike.color">
        <ExBike />
      </ExSection>
      <div class="container">
        <p>
          You might have noticed that the wheels rotate while the bike is moving. 
        </p>
        <p>This is possible because <span class="vue">&lt;kinesis-element></span>s can be nested </p>
                          <p class="code">
         <pre>
          <span class="vue">&lt;kinesis-container></span>
                <span>&lt;svg></span>
                //svg stuff
                    <span class="vue">&lt;kinesis-element</span> 
                          tag="g"
                          :strength="300"
                          type="translate"
                          axis="x"<span class="vue">></span>
                          <span class="vue">&lt;kinesis-element</span> 
                              tag="g"
                              :strength="200"
                              type="rotate"
                              :transformOrigin="backWheelOrigin"
                              axis="x"<span class="vue"> /></span>
                          <span class="vue">&lt;kinesis-element</span> 
                              tag="g"
                              :strength="200"
                              type="rotate"
                              :transformOrigin="frontWheelOrigin"
                              axis="x"<span class="vue"> /></span>
                      <span class="vue">&lt;/kinesis-element></span>
                <span>&lt;/svg></span>
         <span class="vue"> &lt;/kinesis-container></span>
        </pre>
      </p>
      </div>
      <ExSection class="example-section rigging" :bubble="sections.skull.bubble" :color="sections.skull.color">
        <ExRigging />
      </ExSection>
      <div class="container">
        <p>It won't be relevant to show a sample code for this example, as it is similar to the previous one.</p>
        <p>Careful nesting of <span class="vue">&lt;kinesis-element></span>s and good positioning of <span>transformOrigin</span> were the keys for this result.</p>
      </div>

            <ExSection class="example-section" :bubble="sections.orbit.bubble" :color="sections.orbit.color">
 <ExOrbit></ExOrbit>
      </ExSection>
      <div class="container">
        <p><span class="vue">&lt;kinesis-container></span> can react to mouse movement, gyroscope, audio and also to the scroll.</p>
                                  <p class="code">
         <pre>
          <span class="vue">&lt;kinesis-container</span>
            event="scroll"<span class="vue">></span>
                <span class="vue">&lt;kinesis-element</span> 
                      tag="img"
                      :transformOrigin="sunOrigin"
                      :strength="100"
                      type="rotate"<span class="vue">/></span>
                <span class="vue">&lt;kinesis-element</span>
                      :transformOrigin="earthmoonOrigin"
                      :strength="800"
                      type="rotate"<span class="vue">></span>
                      <span>&lt;img</span> :src="earth" <span>/></span>
                      <span class="vue">&lt;kinesis-element</span>
                      tag="img"
                      :src="moon"
                      :transformOrigin="moonOrigin"
                      :strength="1200"
                      type="rotate"<span class="vue">/></span>
                <span class="vue">&lt;/kinesis-element></span>
                <span class="vue">&lt;kinesis-element</span>
                      tag="img"
                      :src="mars"
                      :transformOrigin="marsOrigin"
                      :strength="500"
                      type="rotate"<span class="vue">/></span>
         <span class="vue"> &lt;/kinesis-container></span>
        </pre>
      </p>
      <p>
        If you want an individual element to react to scroll without being inside a <span class="vue">&lt;kinesis-container></span> you can use <span class="vue">&lt;kinesis-scroll></span>. It's used on the <span>Music</span> and <span>Photography</span> demos.
      </p>
      <p></p>
      <p>Similarly, <span class="vue">&lt;kinesis-distance></span> is an individual component that reacts to the mouse's distance from the center. This one is still experimental though, and wouldn't recommend using it for now.</p>
      </div>
      <div class="center">
      <KinesisDistance class="distance-test" :strength="50" :duration="100"></KinesisDistance>

      </div>
      <div class="container">
         <p class="code">
         <pre>
            <span class="vue">&lt;KinesisDistance</span> 
                  :strength="50" 
                  :distance="100"
            <span class="vue">/></span>
        </pre>
      </p>
      </div>

      <p class="center havefun">Have Fun!</p>
      <div class="footer">
              <p class="">Made by <a href="https://www.aminerman.com" target="_blank">Amine Bouyarmane</a></p>
      <a href="https://twitter.com/Aminerman/" target="_blank"><img src="../assets/twitter.svg" height="30" alt=""></a>
      </div>
    </section>
  </div>
</template>

<script>
/* eslint-disable vue/no-unused-components */

import Header from "@/components/Header.vue";
import ExSection from '@/components/ExSection.vue'
import ExMusic from '@/components/ExMusic.vue'
import ExPhotography from '@/components/ExPhotography.vue'
import ExBike from '@/components/ExBike.vue'
import ExRigging from '@/components/ExRigging.vue'
import ExScroll from '@/components/ExScroll.vue'
import { KinesisScroll } from 'vue-kinesis'
import { KinesisDistance } from 'vue-kinesis'
import ExOrbit from '@/components/ExOrbit.vue'

export default {
  name: "home",
  data () {
    return {
      sections: {
        musics: {
          bubble: 'I love music, and a lot of it went into making these components. So here is a sample from each of my favorite genres.',
          color: "#2C525E"
        },
        photography: {
          bubble: 'I\'m quite passionate about photography. Here is a sneak peek  of my work.',
          color: "#44374C"
        },
        bike: {
          bubble: 'I enjoy being in the moutains, either for a hike or for biking.',
          color: "#2C435D",
        },
        
        skull: {
          bubble: 'While making the previous demo, I thought that it might be nice to take it a step further and try to make a more advanced rigging animation. Here is the result.',
          color: "#964040",
        },
                
        orbit: {
          bubble: 'Kinesis can also move a solar system!',
          color: "#222938",
        }
      }
    }
  },
  components: {
    Header,
    ExMusic,
    ExSection,
    ExPhotography,
    ExBike,
    ExRigging,
    ExScroll,
    KinesisScroll,
    KinesisDistance,
    ExOrbit
  }
};
</script>

<style lang="scss" scoped>
.center {
  text-align: center;
}

.container {
  width: 800px;
  max-width: 90%;
  margin: 0 auto;
}

h2 {
  font-size: 25px;
  margin-top: 30px;
}

.intro {
  margin-top: 130px;

}
  p {
    margin-top: 30px;
  }

  span {
    font-weight: 700;
  }

  .vue {
    color: $color-darkgreen;
  }

  .code {
    background-color: $color-blue;
    padding: 10px 20px;
    border-radius: 10px;
    margin: 0 auto;
    margin-top: 30px;
    max-width: 600px;
  }

.examples {
  padding-top: 50px;

  p {
    margin-top: 20px;
    line-height: 20px;
  }
}

.example-section {
  margin-top: 30px;
}

.distance-test {
  width: 60px;
  height: 60px;
  background-color: $color-darkgreen;
  display: inline-block;
  border-radius: 100%;
  margin: 10px;
}

$circle-size: 4vw;



.circle {
  border-radius: 100%;
  position: absolute;

  &.circle-blue {
    background-color: white;
    opacity: 0.1;
    width: $circle-size * 4;
    height: $circle-size * 4;
    top: 10%;
    right: -$circle-size * 2;
  }

  &.circle-purple {
    border: 10px solid #F2EAFA;
    opacity: 0.1;
    width: 4vw;
    height: 4vw;
    left: 10%;
    top: 25%;
  }
    &.circle-purple-2 {
    border: 15px solid #F2EAFA;
    opacity: 0.1;
    width: 8vw;
    height: 8vw;
    right: 10%;
    bottom: 17%;
  }
      &.circle-purple-3 {
    background-color: #F2EAFA;
    opacity: 0.1;
    width: 2vw;
    height: 2vw;
    left: 20%;
    bottom: 17%;
  }

  &.circle-yellow {
    background-color: $color-yellow;
    width: $circle-size * 2;
    height: $circle-size * 2;
    left: 30%;
    top: 25%;
  }

  &.circle-green {
    background-color: $color-green;
    width: $circle-size * 2.5;
    height: $circle-size * 2.5;
    right: 20%;
    top: 90%;
  }

  &.circle-orange {
    border: 25px solid $color-orange;
    width: $circle-size * 2;
    height: $circle-size * 2;
    left: 20%;
    top: 60%;
  }

  &.circle-red {
    background-color: $color-red;
    width: $circle-size * 3;
    height: $circle-size * 3;
    left: -$circle-size * 1.2;
    top: 25%;
  }
}

.examples p.havefun {
    font-family: "Hammersmith One", sans-serif;
    color: $color-brown;
    font-size: 50px;
    margin-top: 50px;
}

.footer {
  margin-top: 50px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  p {
    margin-top: 0;
  }

  a {
    color: $color-brown;
  }
}
</style>
